import moment from 'moment';
import {
    ChatHistoryData,
    ChatDetailData,
    ChatDetailMessage,
    AIContent,
    FeedbackData,
    AIContentCitation,    AIContentDorcResult,
    ChatSessionResponse
} from '../../types/sidekick';

// Type for the transformed chat session response
export interface TransformedChatSessionResponse {
    data: ChatDetailMessage[];
    totalCount: number;
    totalPages: number;
    page: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    conversationId: string;
}

/**
 * Type guard to check if content is AIContent
 */
export const isAIContent = (content: string | AIContent): content is AIContent => {
    return typeof content !== 'string' && content !== null && content !== undefined;
};

/**
 * Type guard to check if feedback is FeedbackData
 */
export const isFeedbackData = (feedback: string | null | FeedbackData): feedback is FeedbackData => {
    return typeof feedback === 'object' && feedback !== null;
};

/**
 * Transform API response to chat session details format
 */
/**
 * Transform API response to chat session details format
 *
 * @param response The API response to transform
 * @param conversationId The conversation ID
 * @returns Transformed response with consistent structure
 */
export const transformChatSessionResponse = (
    response: Partial<ChatSessionResponse>,
    conversationId: string
): TransformedChatSessionResponse => {
    try {
        // Check if the response has the expected structure
        if (response && response.data && Array.isArray(response.data) && 
            response.data[0] && Array.isArray(response.data[0].messages)) {
            return {
                data: response.data[0].messages,
                totalCount: response.totalCount || 0,
                totalPages: response.totalPages || 1,
                page: response.page || 1,
                pageSize: response.pageSize || 10,
                hasNextPage: response.hasNextPage || false,
                hasPreviousPage: response.hasPreviousPage || false,
                conversationId
            };
        } else {
            console.error('Unexpected API response structure:', response);
            throw new Error('Invalid API response structure');
        }
    } catch (error) {
        console.error('Error transforming chat session response:', error);
        throw error;
    }
};

/**
 * Cache for memoized timestamp formatting
 * Limited to 1000 entries to prevent memory issues
 */
const timestampCache = new Map<string, string>();
const MAX_CACHE_SIZE = 1000;

/**
 * Format a timestamp using moment.js in the user's local timezone with memoization
 * Handles null/empty values gracefully
 *
 * @param timestamp The timestamp to format
 * @returns Formatted timestamp string or 'NA' if invalid
 */
export const formatTimestamp = (timestamp: string | null | undefined): string => {
    // Handle null/undefined/empty values
    if (!timestamp) {
        return 'NA';
    }
    
    // Return from cache if available
    if (timestampCache.has(timestamp)) {
        return timestampCache.get(timestamp)!;
    }
    
    try {
        // Format the date in the user's local timezone
        const formattedDate = moment(timestamp).format('DD/MM/YYYY HH:mm:ss');
        
        // Store in cache for future use, with size limit
        if (timestampCache.size >= MAX_CACHE_SIZE) {
            // Remove oldest entry (first key) when cache is full
            const firstKey = timestampCache.keys().next().value;
            if (firstKey !== undefined) {
                timestampCache.delete(firstKey);
            }
        }
        
        timestampCache.set(timestamp, formattedDate);
        
        return formattedDate;
    } catch (error) {
        console.error('Error formatting timestamp:', error);
        return 'Invalid Date';
    }
};

/**
 * Transform feedback data to display format
 */
export const transformFeedback = (feedback: string | null | FeedbackData): string => {
    if (isFeedbackData(feedback)) {
        return feedback.sentiment || 'NA';
    }
    return feedback || 'NA';
};

/**
 * Format citations to display format
 */
export const formatCitations = (citations?: AIContentCitation[]): string => {
    if (!citations || citations.length === 0) {
        return 'NA';
    }
    return citations.map((citation) => {
        return citation.id && citation.section ? `${citation.id}[${citation.section}]` : 'NA';
    }).join(', ');
};

/**
 * Format DORC citations to display format
 */
export const formatDorcCitations = (dorc?: AIContentDorcResult): string => {
    if (!dorc || !dorc.panviva_document_id) {
        return 'NA';
    }
    return dorc.panviva_document_id;
};

/**
 * Extract AI response content from message
 */
export const extractAIResponseContent = (message: ChatDetailMessage): string => {
    if (isAIContent(message.content) && message.content.ai_results?.response) {
        return message.content.ai_results.response;
    }
    return typeof message.content === 'string' ? message.content : '';
};

/**
 * Truncate text to a specified number of words
 * If the text has more words than the limit, it appends "..." at the end
 *
 * @param text The text to truncate
 * @param wordLimit The maximum number of words to display
 * @returns Object with truncated text and flag indicating if truncation occurred
 */
export const truncateToWords = (text: string, wordLimit: number = 10): { text: string; isTruncated: boolean } => {
    if (!text) {
        return { text: '', isTruncated: false };
    }

    const words = text.trim().split(/\s+/);

    if (words.length <= wordLimit) {
        return { text: words.join(' '), isTruncated: false };
    }

    return { text: words.slice(0, wordLimit).join(' ') + '...', isTruncated: true };
};